/**
 * Pure layout algorithm for the workflow flow graph.
 *
 * Takes a list of FlowStep nodes and computes pixel positions for each node
 * plus directed edges between them. No React, no side-effects — just math.
 *
 * Algorithm:
 *   1. Build adjacency from `after` fields.
 *   2. Topological sort to assign y-levels (rows).
 *   3. Center columns within each row relative to the widest row.
 *   4. Generate edges with optional branch labels.
 */

import type { FlowStep, NodePosition, FlowEdge } from "./types";

// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------

export const NODE_WIDTH = 160;
export const NODE_HEIGHT = 36;
export const GAP_X = 32;
export const GAP_Y = 48;

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

export interface LayoutResult {
  positions: NodePosition[];
  edges: FlowEdge[];
  totalWidth: number;
  totalHeight: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Return the pixel height for a step (uniform for all types). */
function nodeHeight(_step: FlowStep): number {
  return NODE_HEIGHT;
}

/** Normalize `after` into a flat array of predecessor IDs. */
function predecessors(step: FlowStep): string[] {
  if (step.after === null) return [];
  if (typeof step.after === "string") return [step.after];
  return step.after;
}

/**
 * Detect whether a step participates in a cycle.
 *
 * We do a simple DFS from `startId` following the forward adjacency map.
 * If we can reach `startId` again, there is a cycle involving that node.
 */
function isInCycle(
  stepId: string,
  forwardAdj: Map<string, string[]>,
  stepIndex: Map<string, FlowStep>,
): boolean {
  const visited = new Set<string>();
  const stack = [...(forwardAdj.get(stepId) ?? [])];
  while (stack.length > 0) {
    const current = stack.pop()!;
    if (current === stepId) return true;
    if (visited.has(current)) continue;
    if (!stepIndex.has(current)) continue;
    visited.add(current);
    for (const next of forwardAdj.get(current) ?? []) {
      stack.push(next);
    }
  }
  return false;
}

// ---------------------------------------------------------------------------
// Main layout function
// ---------------------------------------------------------------------------

export function computeFlowLayout(steps: FlowStep[]): LayoutResult {
  if (steps.length === 0) {
    return { positions: [], edges: [], totalWidth: 0, totalHeight: 0 };
  }

  // Index steps by ID for quick lookup.
  const stepIndex = new Map<string, FlowStep>();
  for (const step of steps) {
    stepIndex.set(step.id, step);
  }

  // Build forward adjacency: parent -> children (who lists parent in `after`).
  const forwardAdj = new Map<string, string[]>();
  for (const step of steps) {
    for (const predId of predecessors(step)) {
      if (!forwardAdj.has(predId)) forwardAdj.set(predId, []);
      forwardAdj.get(predId)!.push(step.id);
    }
  }

  // -----------------------------------------------------------------------
  // 1. Assign levels via topological ordering
  // -----------------------------------------------------------------------

  // Filter out steps that participate in cycles — they cannot be placed.
  const validIds = new Set<string>();
  for (const step of steps) {
    if (!isInCycle(step.id, forwardAdj, stepIndex)) {
      validIds.add(step.id);
    }
  }

  // Level assignment: root steps (after === null or all preds missing/cyclic) = 0.
  // A step's level = max(level of valid predecessors) + 1.
  const levels = new Map<string, number>();

  function assignLevel(id: string): number {
    if (levels.has(id)) return levels.get(id)!;
    // Temporarily mark to detect recursive calls (shouldn't happen after
    // cycle filtering, but guard defensively).
    levels.set(id, 0);

    const step = stepIndex.get(id)!;
    const preds = predecessors(step).filter(
      (pid) => validIds.has(pid) && stepIndex.has(pid),
    );

    let maxPredLevel = -1;
    for (const pid of preds) {
      maxPredLevel = Math.max(maxPredLevel, assignLevel(pid));
    }

    const level = maxPredLevel + 1;
    levels.set(id, level);
    return level;
  }

  for (const id of validIds) {
    assignLevel(id);
  }

  // -----------------------------------------------------------------------
  // 2. Group steps into rows by level
  // -----------------------------------------------------------------------

  const rowMap = new Map<number, string[]>();
  for (const id of validIds) {
    const lvl = levels.get(id)!;
    if (!rowMap.has(lvl)) rowMap.set(lvl, []);
    rowMap.get(lvl)!.push(id);
  }

  const sortedLevels = [...rowMap.keys()].sort((a, b) => a - b);

  // -----------------------------------------------------------------------
  // 3. Compute widths for each row and find the widest
  // -----------------------------------------------------------------------

  function rowWidth(ids: string[]): number {
    return ids.length * NODE_WIDTH + (ids.length - 1) * GAP_X;
  }

  let maxRowWidth = 0;
  for (const lvl of sortedLevels) {
    const w = rowWidth(rowMap.get(lvl)!);
    if (w > maxRowWidth) maxRowWidth = w;
  }

  // -----------------------------------------------------------------------
  // 4. Position nodes — center each row horizontally
  // -----------------------------------------------------------------------

  const positions: NodePosition[] = [];
  let currentY = 0;

  for (const lvl of sortedLevels) {
    const ids = rowMap.get(lvl)!;
    const rw = rowWidth(ids);
    const offsetX = (maxRowWidth - rw) / 2;

    // Find the tallest node in this row for consistent row spacing.
    let rowMaxHeight = 0;
    for (const id of ids) {
      const h = nodeHeight(stepIndex.get(id)!);
      if (h > rowMaxHeight) rowMaxHeight = h;
    }

    for (let i = 0; i < ids.length; i++) {
      const stepId = ids[i]!;
      const step = stepIndex.get(stepId)!;
      const h = nodeHeight(step);
      // Vertically center shorter nodes within the row's tallest node.
      const yOffset = (rowMaxHeight - h) / 2;

      positions.push({
        stepId,
        x: offsetX + i * (NODE_WIDTH + GAP_X),
        y: currentY + yOffset,
        width: NODE_WIDTH,
        height: h,
      });
    }

    currentY += rowMaxHeight + GAP_Y;
  }

  // -----------------------------------------------------------------------
  // 5. Generate edges
  // -----------------------------------------------------------------------

  const edges: FlowEdge[] = [];

  for (const id of validIds) {
    const step = stepIndex.get(id)!;
    const preds = predecessors(step).filter(
      (pid) => validIds.has(pid) && stepIndex.has(pid),
    );

    for (const predId of preds) {
      const parentStep = stepIndex.get(predId)!;
      let label: string | undefined;

      // For conditional parents, find the branch label that points to this step.
      if (parentStep.type === "conditional" && parentStep.branches) {
        const branch = parentStep.branches.find(
          (b) => b.targetStepId === id,
        );
        if (branch) {
          label = branch.label;
        }
      }

      edges.push({
        fromStepId: predId,
        toStepId: id,
        ...(label !== undefined && { label }),
      });
    }
  }

  // -----------------------------------------------------------------------
  // 6. Compute total bounds
  // -----------------------------------------------------------------------

  const totalWidth = maxRowWidth;
  // currentY already includes the last GAP_Y, remove it.
  const totalHeight = sortedLevels.length > 0 ? currentY - GAP_Y : 0;

  return { positions, edges, totalWidth, totalHeight };
}
